import { TextField as MuiTextField } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import PropTypes from 'prop-types';
import {
  SCHEDULE_BLOCK,
  addDaysIso,
  completeLines,
  scheduleTotal,
  todayIso,
} from '../utils/fillLaterSchedule';

/**
 * Bulk Update — the fill later schedule the apply bar stamps across the selection.
 *
 * A list, not one date. The operator builds the whole schedule here once and pushes it onto every
 * selected order (POM-6167: split in bulk, instead of one at a time from the patient profile);
 * each row then holds its own copy and can still be adjusted on its own afterwards.
 *
 * Its own file rather than another block inside BulkOverride.js: that file sits at the `max-lines`
 * ceiling, which counts code and not the comments around it.
 */
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/* The picker speaks Date objects; the line stores the plain YYYY-MM-DD the API takes. Local noon,
 * matching fillLaterSchedule's own convention -- a Date built at midnight crosses to the
 * neighbouring day under some browser offsets, and these are calendar days, never instants. */
const toDate = (iso) => {
  if (!ISO_DAY.test(iso || '')) return null;

  const [year, month, day] = iso.split('-').map(Number);

  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

const isoFromDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

  const pad = (n) => String(n).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const FillLaterMoveDateField = (props) => {
  const { value, issue, isDisabled, disabledTip, onChange } = props;
  const earliestDate = toDate(addDaysIso(todayIso(), 1));

  return (
    <div className='ab-sched' data-tip={isDisabled ? disabledTip : undefined}>
      <div className='ab-sched-head'>
        <span className='mini'>
          Fill later date <span className='req'>*</span>
        </span>
      </div>

      <div className='ab-sched-row'>
        <div className={`ab-sched-date${isDisabled ? ' off' : ''}`} style={{ width: 172 }}>
          <DesktopDatePicker
            label='Fill date'
            inputFormat='MM/dd/yyyy'
            minDate={earliestDate}
            disabled={isDisabled}
            value={toDate(value)}
            onChange={(date) => onChange(isoFromDate(date))}
            renderInput={(params) => (
              <MuiTextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                size='small'
                fullWidth
                error={Boolean(issue)}
                inputProps={{ ...params.inputProps, 'aria-label': 'Fill date' }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </div>
      </div>

      {issue && (
        <div className='ab-sched-foot'>
          <span className='ab-sched-bad'>{issue.message}</span>
        </div>
      )}
    </div>
  );
};

FillLaterMoveDateField.propTypes = {
  value: PropTypes.string,
  issue: PropTypes.shape({ message: PropTypes.string }),
  isDisabled: PropTypes.bool,
  disabledTip: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

FillLaterMoveDateField.defaultProps = {
  value: '',
  issue: null,
  isDisabled: false,
  disabledTip: undefined,
};

const FillLaterScheduleFields = (props) => {
  const {
    schedule,
    issue,
    dispenseQuantity,
    isDisabled,
    disabledTip,
    onLineChange,
    onAddLine,
    onRemoveLine,
  } = props;

  /* POM-6197 turned the lead time into the prefill and left "after today" as the only rule,
     because the order is not created on the chosen date anyway -- it waits for the earlier item
     to ship. The calendar refuses an earlier day; a typed one is caught by scheduleProblem, which
     marks the line, since a text field has no min to enforce. */
  const earliestDate = toDate(addDaysIso(todayIso(), 1));
  const total = scheduleTotal(schedule);
  const filled = completeLines(schedule).length;
  /* A second split cannot be started while an earlier one is still blank: the operator would be
     building a schedule whose first line can never be applied. */
  const hasIncompleteLine = filled < schedule.length;
  /* The original has to keep something to fill now, so one less than the row's quantity is the
     most that can ever come off it. Caps the spinner; the typed case is caught by `issue`. */
  const available = Number(dispenseQuantity);
  const mostPerSplit = Number.isFinite(available) && available > 1 ? available - 1 : undefined;
  const isOverQuantity = issue?.reason === SCHEDULE_BLOCK.EXCEEDS_QUANTITY;

  return (
    <div className='ab-sched' data-tip={isDisabled ? disabledTip : undefined}>
      <div className='ab-sched-head'>
        <span className='mini'>
          Fill later schedule <span className='req'>*</span>
        </span>
        {filled > 0 && (
          <span className={`ab-sched-tally${isOverQuantity ? ' bad' : ''}`}>
            {filled === 1
              ? `Takes ${total} from each order`
              : `Takes ${total} from each order across ${filled} dates`}
          </span>
        )}
      </div>

      {schedule.map((line, index) => (
        /* Index as the key deliberately: a schedule line has no id, and removing one shifts every
           line after it. Keying on the values instead would remount the field being typed into and
           drop focus on every keystroke. */
        /* eslint-disable-next-line react/no-array-index-key */
        <div className='ab-sched-row' key={index}>
          <span className='ab-sched-seq'>{index + 1}</span>

          <div
            className={`nf float on${isDisabled ? ' off' : ''}${isOverQuantity ? ' bad' : ''}`}
            style={{ width: 176 }}
          >
            <span className='mini'>Quantity to fill later</span>
            <input
              type='number'
              min='1'
              max={mostPerSplit}
              step='1'
              disabled={isDisabled}
              aria-label={`Quantity to fill later, split ${index + 1}`}
              value={line.quantity}
              onChange={(event) =>
                onLineChange({ index, field: 'quantity', value: event.target.value })
              }
            />
          </div>

          {/* POM-6184 (QA): the mask read dd-mm-yyyy here and mm/dd/yyyy on every other screen. A
              native date input renders its mask in the BROWSER's locale and cannot be told
              otherwise, so this is the Add Stock BUD field's arrangement -- DesktopDatePicker with
              inputFormat, rendered into an outlined MuiTextField -- which gives the same MM/DD/YYYY
              and the same calendar button inside the box, with no styling of our own to keep in
              step. The line still stores the same YYYY-MM-DD it always did. */}
          <div className={`ab-sched-date${isDisabled ? ' off' : ''}`} style={{ width: 172 }}>
            <DesktopDatePicker
              label='Fill date'
              inputFormat='MM/dd/yyyy'
              minDate={earliestDate}
              disabled={isDisabled}
              value={toDate(line.scheduledFor)}
              onChange={(date) =>
                onLineChange({ index, field: 'scheduledFor', value: isoFromDate(date) })
              }
              renderInput={(params) => (
                <MuiTextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  size='small'
                  fullWidth
                  error={issue?.reason === SCHEDULE_BLOCK.PAST_DATE && issue.lineIndex === index}
                  inputProps={{
                    ...params.inputProps,
                    'aria-label': `Fill date, split ${index + 1}`,
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </div>

          <button
            type='button'
            className='ab-sched-x'
            disabled={isDisabled || schedule.length <= 1}
            aria-label={`Remove split ${index + 1}`}
            onClick={() => onRemoveLine(index)}
          >
            &#10005;
          </button>
        </div>
      ))}

      {/* Right-aligned under the ✕ column. The standing hint lives in the bar's copy column on
          the left; only a live problem speaks here, where the operator is already looking. */}
      <div className='ab-sched-foot'>
        {issue && <span className='ab-sched-bad'>{issue.message}</span>}
        <button
          type='button'
          className='ab-sched-add'
          disabled={isDisabled || hasIncompleteLine}
          title={hasIncompleteLine ? 'Complete the current split first' : undefined}
          onClick={onAddLine}
        >
          + Add more split
        </button>
      </div>
    </div>
  );
};

FillLaterScheduleFields.propTypes = {
  schedule: PropTypes.arrayOf(
    PropTypes.shape({
      quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      scheduledFor: PropTypes.string,
    })
  ).isRequired,
  issue: PropTypes.shape({
    reason: PropTypes.string,
    lineIndex: PropTypes.number,
    message: PropTypes.string,
  }),
  dispenseQuantity: PropTypes.string,
  isDisabled: PropTypes.bool,
  disabledTip: PropTypes.string,
  onLineChange: PropTypes.func.isRequired,
  onAddLine: PropTypes.func.isRequired,
  onRemoveLine: PropTypes.func.isRequired,
};

FillLaterScheduleFields.defaultProps = {
  issue: null,
  dispenseQuantity: '',
  isDisabled: false,
  disabledTip: undefined,
};

export default FillLaterScheduleFields;
